import { useConnection as useWagmiConnection } from "wagmi";
import { useState, useEffect } from "react";
import { STORAGE_KEYS } from "@/lib/storageKeys";

interface GhostMode {
  enabled: boolean;
  address: `0x${string}`;
}

function getGhostMode(): GhostMode | null {
  if (typeof window === "undefined") return null;

  // Check URL param first: ?ghost=0x... or ?ghost= (empty clears ghost)
  const params = new URLSearchParams(window.location.search);
  if (params.has("ghost")) {
    const ghostParam = params.get("ghost");
    if (ghostParam && /^0x[0-9a-fA-F]{40}$/.test(ghostParam)) {
      const ghost = { enabled: true, address: ghostParam as `0x${string}` };
      localStorage.setItem(STORAGE_KEYS.GHOST_MODE, JSON.stringify(ghost));
      return ghost;
    }
    // Empty or invalid ?ghost= clears ghost mode
    localStorage.removeItem(STORAGE_KEYS.GHOST_MODE);
    return null;
  }

  // Fall back to localStorage
  const stored = localStorage.getItem(STORAGE_KEYS.GHOST_MODE);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Custom hook that wraps wagmi's useConnection.
 * When ghost mode is enabled, returns mock connected state.
 *
 * Activation options:
 * 1. URL param:   ?ghost=0x...
 * 2. localStorage: set STORAGE_KEYS.GHOST_MODE to { enabled: true, address: "0x..." }
 */
export function useConnection() {
  const wagmiConnection = useWagmiConnection();
  const [ghost, setGhost] = useState<GhostMode | null>(() => getGhostMode());

  useEffect(() => {
    // Read ghost mode on mount
    setGhost(getGhostMode());

    // Listen for storage changes from other tabs
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.GHOST_MODE) {
        setGhost(getGhostMode());
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  if (ghost?.enabled && ghost.address) {
    return {
      ...wagmiConnection,
      address: ghost.address,
      addresses: [ghost.address] as readonly `0x${string}`[],
      isConnected: true,
      isConnecting: false,
      isDisconnected: false,
      isReconnecting: false,
      status: "connected" as const,
      isGhostMode: true,
      detachGhost: () => {
        localStorage.removeItem(STORAGE_KEYS.GHOST_MODE);
        window.location.href = window.location.pathname;
      },
    };
  }

  return { ...wagmiConnection, isGhostMode: false, detachGhost: undefined };
}
